import format from 'date-fns/format'

const startOfToday = format(new Date(), "yyyy-MM-dd'T00:00:00'xxx")
const endOfToday = format(new Date(), "yyyy-MM-dd'T23:59:59'xxx")

// Action Types
export const SET_CALLS = 'callSearch/setCalls'
export const SET_CALL_EXPLORER_CALL = 'callSearch/setCallExplorerCall'
export const SET_CALL_EXPLORER_DATA = 'callSearch/setCallExplorerData'
export const SET_DATA = 'callSearch/setData'
export const SET_FILTERS = 'callSearch/setFilters'
export const CLEAR_FILTERS_AND_SELECT_ORG = 'callSearch/clearFiltersAndSelectOrg'
export const SET_DATE_RANGE_FILTERS = 'callSearch/setDateRangeFilters'
export const SET_LOADING = 'callSearch/setLoading'
export const SET_ERROR = 'callSearch/setError'
export const SET_CALL_EXPLORER_LOADING = 'callSearch/setCallExplorerLoading'
export const SET_TOTAL_COUNT = 'callSearch/setTotalCount'
export const SET_CSV_ABORT_CONTROLLER = 'callSearch/setCsvAbortController'

// Action Creators
export const setCalls = (payload) => ({ type: SET_CALLS, payload })
export const setCallExplorerCall = (payload) => ({ type: SET_CALL_EXPLORER_CALL, payload })
export const setCallExplorerData = (payload) => ({ type: SET_CALL_EXPLORER_DATA, payload })
export const setData = (payload) => ({ type: SET_DATA, payload })
export const setFilters = (payload) => ({ type: SET_FILTERS, payload })
export const clearFiltersAndSelectOrg = (payload) => ({ type: CLEAR_FILTERS_AND_SELECT_ORG, payload }) // prettier-ignore
export const setDateRangeFilters = (payload) => ({ type: SET_DATE_RANGE_FILTERS, payload })
export const setLoading = (payload) => ({ type: SET_LOADING, payload })
export const setError = (payload) => ({ type: SET_ERROR, payload })
export const setCallExplorerLoading = (payload) => ({ type: SET_CALL_EXPLORER_LOADING, payload })
export const setTotalCount = (payload) => ({ type: SET_TOTAL_COUNT, payload })
export const setCsvAbortController = (payload) => ({ type: SET_CSV_ABORT_CONTROLLER, payload })

// State
export const initialState = {
  calls: [],
  totalCount: null,
  callExplorer: {
    callId: '',
    analysis: [],
    analysisFlattened: [],
    metadata: {},
    transcript: [],
    qaScores: [],
    callSummary: {},
    copilotAnchors: [],
    agentActivityEvents: [],
    associatedCallIds: [],
    selectedCriteriaFilter: 'All Criteria',
    selectedCriteriaFocusIndex: 0,
    callComments: [],
    playbook: {},
    extracted_data: {},
  },
  data: {
    selectedSavedFilterId: null,
    agents: [],
    tags: [],
    playbooks: [],
    playbookData: [],
    voipCustomerCalls: [],
    voipCampaigns: [],
    voipCampaignsTimedOut: false,
    outboundCampaigns: [],
    // voice agent outcomes and disconnection reasons
    aiAgentOutcomes: [],
    aiAgentDisconnectionReasons: [],
    dispositions: [],
    scorecardConfigs: [],
    // Org's filterable pick_list bit fields (each with its selectable options).
    bitFields: [],
  },
  filters: {
    startDate: startOfToday,
    endDate: endOfToday,
    // Saved-filter key of the clicked preset; kept here so dismissing the Filters modal restores it too.
    dateRange: undefined,
    organizationId: '',
    agents: [],
    tags: [],
    playbooks: [],
    callDuration: '1',
    maxCallDuration: '',
    isWin: '',
    includeCallsWithoutAudio: false,
    keywords: [],
    keywordOptions: { side: 'both', logic: 'or' },
    checklist: { selected: [], includes: true },
    deck: { selected: [], includes: true },
    postcall: { selected: [], includes: true },
    notifications: { selected: [], includes: true },
    openCategories: [],
    openSubcategories: [],
    dispositions: { selected: [], includes: true },
    scoredStatus: '',
    scorecards: [],
    scorecardThreshold: '',
    voipCampaigns: [],
    outboundCampaigns: [],
    aiAgentOutcomes: [],
    aiAgentDisconnectionReasons: [],
    minMessageCount: '',
    maxMessageCount: '',
    channel: 'call',
    voipCustomerIds: [],
    endingSentiment: [],
    attachments: [],
    direction: '',
    agentType: '',
    // Selected pick_list options keyed by field_id: { [field_id]: [{ value, label }] }.
    // Session-only by design: like voip/outbound campaign filters, this is not
    // round-tripped through the URL or saved filters (callSearch/helpers.js), so
    // selections reset on refresh. Full URL/saved-filter parity is a follow-up.
    bitFields: {},
    // Selected value for boolean bit fields keyed by field_id: { [field_id]: true|false }.
    // Session-only like bitFields above.
    booleanFields: {},
  },
  loading: {
    calls: false,
    tags: false,
    playbooks: false,
    playbookData: false,
    qaScores: false,
    callSummary: false,
    summarySearch: false,
    dispositions: false,
    csvDownload: false,
    voipCampaigns: false,
    voipCustomerIds: false,
    outboundCampaigns: false,
    aiAgentOutcomes: false,
    aiAgentDisconnectionReasons: false,
    bitFields: false,
  },
  callExplorerLoading: false,
  error: {
    callExplorer: false,
  },
  selectedRows: [],
  csvAbortController: null,
}

export default function callSearchReducer(state = initialState, action) {
  switch (action.type) {
    case SET_CALLS:
      return { ...state, calls: action.payload }
    case SET_CALL_EXPLORER_CALL:
      return { ...state, callExplorer: { ...initialState.callExplorer, ...action.payload } }
    case SET_CALL_EXPLORER_DATA:
      return { ...state, callExplorer: { ...state.callExplorer, ...action.payload } }
    case SET_DATA:
      return { ...state, data: { ...state.data, ...action.payload } }
    case SET_FILTERS:
      return { ...state, filters: { ...state.filters, ...action.payload } }
    case CLEAR_FILTERS_AND_SELECT_ORG:
      return {
        ...state,
        filters: {
          ...initialState.filters,
          organizationId: action.payload,
          channel: state.filters.channel, // Keep selected tab (channel) on organization change & clear filters
        },
      }
    case SET_DATE_RANGE_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          startDate: action.payload.startDate,
          endDate: action.payload.endDate,
          // A date write without a preset clears it, so a stale preset cannot name dates it did not produce.
          dateRange: action.payload.dateRange,
        },
      }
    case SET_LOADING:
      return { ...state, loading: { ...state.loading, ...action.payload } }
    case SET_ERROR:
      return { ...state, error: { ...state.error, ...action.payload } }
    case SET_CALL_EXPLORER_LOADING:
      return { ...state, callExplorerLoading: action.payload }
    case SET_TOTAL_COUNT:
      return { ...state, totalCount: action.payload }
    case SET_CSV_ABORT_CONTROLLER:
      return { ...state, csvAbortController: action.payload }
    default:
      return state
  }
}
